import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Col,
  Container,
  Row,
  Badge,
  Button,
  Dropdown,
  Modal,
  Form
} from "react-bootstrap";
import Chart from "react-apexcharts";
import ScrollToTop from "./common/ScrollToTop";
import DataApi from "../api/dataApi";
import '../App.css';
import jwt_decode from "jwt-decode";
import DashboardApi from "../api/dashboardApi";
import { useNavigate } from "react-router-dom";
// import PlanDropdownCard from "./common/PlanDropdownCard";

const PRIMARY_COLOR = "#4338ca";
const ACCENT_COLOR = "#6366f1";
const SUCCESS_COLOR = "#059669";
const WARNING_COLOR = "#f59e0b";
const DANGER_COLOR = "#dc2626";
const INFO_COLOR = "#8b5cf6";

const styles = {
  card: {
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
    background: "#fff",
    height: "100%",
    transition: "all 0.3s ease",
    overflow: "hidden",
  },
  interactiveCard: {
    cursor: "pointer",
  },
  cardHeader: {
    background: "transparent",
    borderBottom: "1px solid #f1f5f9",
    borderRadius: "16px 16px 0 0",
    padding: "12px 16px"
  },
  cardBody: {
    padding: "16px"
  },
  sectionTitle: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#0f172a",
    marginBottom: "16px",
    marginTop: "20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    paddingLeft: "5px"
  }
};

const AlertCardHoverStyle = {
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.1)",
  }
};

const InteractiveCard = ({ children, style, onClick, ...props }) => {
  const [hover, setHover] = useState(false);
  return (
    <Card
      {...props}
      style={{
        ...styles.card,
        ...styles.interactiveCard,
        ...style,
        ...(hover ? AlertCardHoverStyle["&:hover"] : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      {children}
    </Card>
  );
};

const Dashboard = ({ userInfo: propUserInfo, disabled = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [filter, setFilter] = useState("all");
  const [booksData, setBooksData] = useState([]);
  const [issuesData, setIssuesData] = useState([]);
  const [upcomingDueMembers, setUpcomingDueMembers] = useState([]);
  const [metrics, setMetrics] = useState({
    dueSoonCount: 0,
    overdueCount: 0,
    fineCollectedThisMonth: 0,
    damagedCount: 0,
    totalBooks: 0,
    totalTitles: 0,
    availableBooks: 0,
    issuedBooks: 0,
    booksThisMonth: 0,
    totalSubmissions: 0,
    total_copies: 0,
  });

  const [cardDetails, setCardDetails] = useState([]);
  const [cardLimitSetting, setCardLimitSetting] = useState(6);
  const [categories, setCategories] = useState([]);
  const [topAvailableBooks, setTopAvailableBooks] = useState([]);
  const [latestMembers, setLatestMembers] = useState([]);
  const [booksByCategory, setBooksByCategory] = useState([]);

  const formatNumber = useCallback((num) => {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString('en-IN');
  }, []);

  const formatCurrency = useCallback((val) => {
    const n = Number(val);
    if (!isFinite(n)) return `₹0.00`;
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  useEffect(() => {
    const initializeUser = () => {
      let currentUserInfo = propUserInfo;
      if (!currentUserInfo) {
        try {
          const token = sessionStorage.getItem("token");
          if (token) {
            currentUserInfo = jwt_decode(token);
          }
        } catch (error) {
          console.error("Error decoding token:", error);
        }
      }
      setUserInfo(currentUserInfo);
      setUserRole(currentUserInfo?.userrole?.toUpperCase() || "ADMIN");
    };

    initializeUser();
  }, [propUserInfo]);

  useEffect(() => {
    let isMounted = true;

    const fetchAllDashboardData = async () => {
      try {
        setLoading(true);

        await Promise.all([
          fetchDashboardSummary(),
          fetchAlertMetrics(),
          fetchLibraryDetails(),
          fetchLatestMembers(),
          // fetchAllBooks(),
          fetchAllIssues()
        ]);

      } catch (error) {
        console.error("Error in dashboard data fetch:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAllDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  // const fetchAllBooks = async () => {
  //   try {
  //     const bookApi = new DataApi("book");
  //     const booksResp = await bookApi.fetchAll();
  //     const books = Array.isArray(booksResp?.data) ? booksResp.data :
  //       (booksResp?.data?.rows || booksResp || []);
  //     setBooksData(books);
  //   } catch (error) {
  //     console.error("Error fetching books:", error);
  //   }
  // };

  const fetchAllIssues = async () => {
    try {
      const issueApi = new DataApi("bookissue");
      const issuesResp = await issueApi.fetchAll();
      const issues = Array.isArray(issuesResp?.data) ? issuesResp.data :
        (issuesResp?.data?.rows || issuesResp || []);
      console.log("issues", issues);

      setIssuesData(issues);

      processUpcomingDueDates(issues);

    } catch (error) {
      console.error("Error fetching issues:", error);
    }
  };
  const processUpcomingDueDates = (issues) => {
    if (!Array.isArray(issues)) return;

    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const dayAfterTomorrow = new Date(now);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    dayAfterTomorrow.setHours(0, 0, 0, 0);

    const upcomingIssues = issues.filter(issue => {
      if (!issue.due_date) return false;
      const dueDate = new Date(issue.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate >= tomorrow && dueDate <= dayAfterTomorrow &&
        (issue.status === 'issued' || issue.status === 'active');
    });

    const membersMap = new Map();

    upcomingIssues.forEach(issue => {
      const memberId = issue.member_id || issue.card_id || issue.memberId;
      const dueDate = new Date(issue.due_date);

      if (!membersMap.has(memberId)) {
        membersMap.set(memberId, {
          id: memberId,
          // Map all possible name fields from issues API
          name: issue.member_name || issue.user_name || `${issue.member_firstname || ''} ${issue.member_lastname || ''}`.trim() || `Member ${memberId}`,
          email: issue.member_email || '',
          phone: issue.member_phone || '',
          due_date: formatDate(dueDate),
          due_count: 1,
          books: [issue.book_title || issue.title || 'Unknown Book'],
          card_number: issue.card_number || `CARD${memberId}`,
          status: 'Due Soon',
          // Use the actual image field from issues API
          image: issue.member_image || issue.profile_pic || getDefaultPhoto(memberId)
        });
      } else {
        const member = membersMap.get(memberId);
        member.due_count += 1;
        if (issue.book_title || issue.title) {
          member.books.push(issue.book_title || issue.title);
        }
      }
    });

    const membersList = Array.from(membersMap.values())
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
      .slice(0, 5);

    setUpcomingDueMembers(membersList);
  };
  // const processUpcomingDueDates = (issues) => {
  //   if (!Array.isArray(issues)) return;

  //   const now = new Date();
  //   const tomorrow = new Date(now);
  //   tomorrow.setDate(tomorrow.getDate() + 1);
  //   tomorrow.setHours(0, 0, 0, 0);

  //   const dayAfterTomorrow = new Date(now);
  //   dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  //   dayAfterTomorrow.setHours(0, 0, 0, 0);

  //   const upcomingIssues = issues.filter(issue => {
  //     if (!issue.due_date) return false;

  //     const dueDate = new Date(issue.due_date);
  //     dueDate.setHours(0, 0, 0, 0);

  //     return dueDate >= tomorrow && dueDate <= dayAfterTomorrow &&
  //       (issue.status === 'issued' || issue.status === 'active');
  //   });

  //   const membersMap = new Map();

  //   upcomingIssues.forEach(issue => {
  //     const memberId = issue.member_id || issue.card_id;
  //     const dueDate = new Date(issue.due_date);
  //      
  //      
  //      
  //     if (!membersMap.has(memberId)) {
  //       membersMap.set(memberId, {
  //         id: memberId,
  //         name: issue.member_name || issue.user_name || `Member ${memberId}`,
  //         email: issue.member_email || '',
  //         phone: issue.member_phone || '',
  //         due_date: formatDate(dueDate),
  //         due_count: 1,
  //         books: [issue.book_title || 'Unknown Book'],
  //         card_number: issue.card_number || `CARD${memberId}`,
  //         status: 'Due Soon',
  //         image: issue.member_image || issue.profile_pic || getDefaultPhoto(memberId)
  //       });
  //     } else {
  //       const member = membersMap.get(memberId);
  //       member.due_count += 1;
  //       if (issue.book_title) {
  //         member.books.push(issue.book_title);
  //       }
  //     }
  //   });

  //   const membersList = Array.from(membersMap.values())
  //     .sort((a, b) => {
  //       const dateA = new Date(a.due_date);
  //       const dateB = new Date(b.due_date);
  //       return dateA - dateB;
  //     })
  //     .slice(0, 5);

  //   setUpcomingDueMembers(membersList);

  //    
  // };

  const getDefaultPhoto = (id) => {

    const defaultPhotos = [
      "/default-user.png",
      "https://randomuser.me/api/portraits/men/32.jpg",
      "https://randomuser.me/api/portraits/women/44.jpg",
      "https://randomuser.me/api/portraits/men/67.jpg",
      "https://randomuser.me/api/portraits/women/65.jpg",
    ];
    return defaultPhotos[id % defaultPhotos.length];
  };

  const formatDate = (date) => {
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    };
    return date.toLocaleDateString('en-GB', options);
  };

  const getDueStatusColor = (dueDateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(dueDateStr);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return DANGER_COLOR;
    if (diffDays === 1) return WARNING_COLOR;
    return INFO_COLOR;
  }; const fetchLatestMembers = async () => {
    try {
      const memberApi = new DataApi("librarycard");
      const response = await memberApi.fetchAll();

      let members = [];
      if (response?.data) {
        members = Array.isArray(response.data) ? response.data : (response.data.rows || []);
      }

      // Properly map the API response fields
      const latestMembersSorted = members.map(member => ({
        id: member.id || member.member_id,
        // Check for various possible name fields
        name: `${member.firstname || member.first_name || member.name || ''} ${member.lastname || member.last_name || ''}`.trim() || `Member ${member.id}`,
        email: member.email || '',
        phone: member.phone || member.phone_number || '',
        created_at: member.created_at || member.createdAt,
        // Use the actual profile image field from your API
        image: member.image ? `/uploads/librarycards/${member.image}` : getDefaultPhoto(member.id),
        card_number: member.card_number || `CARD${member.id}`,
        status: member.status || 'active'
      }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);



      setLatestMembers(latestMembersSorted);

    } catch (error) {
      console.error("Error fetching latest members:", error);

      setLatestMembers([]);
    }
  };



  const fetchDashboardSummary = async () => {
    try {
      console.log("category api response:", categories);
      const libraryApi = new DataApi("library");
      const dashboardResponse = await libraryApi.get("/dashboard");
      console.log("Dashboard API Response:", dashboardResponse);
      console.log("Dashboard API Data:", libraryApi);
      if (dashboardResponse.data?.success) {
        const data = dashboardResponse.data.data;
        console.log("data.summary.totalBooks ", data)
        setDashboardData(data);
        if (data.summary) {
          setMetrics(prev => ({
            ...prev,
            totalBooks: data.summary.totalBooks || data.summary.total_copies || 0,
            totalTitles: data.summary.totalTitles || data.summary.total_books || 0,
            availableBooks: data.summary.availableBooks || data.summary.available_copies || 0,
            issuedBooks: data.summary.issuedBooks || data.summary.issued_copies || 0,
            damagedCount: data.summary.dammegedbooks || data.summary.dammegedbooks || 0,
            booksThisMonth: data.summary.booksThisMonth || data.summary.books_this_month || 0,
            totalSubmissions: data.summary.totalSubmissions || data.summary.total_submissions || 0,
            total_copies: data.summary.totalCopies || data.summary.totalCopies || 0,
          }));
        }
        if (data.booksByCategory?.length > 0) {
          setBooksByCategory(data.booksByCategory);
          const topCategories = data.booksByCategory.slice(0, 5).map(item => ({
            name: item.category_name || "Unknown",
            icon: "fa-tag",
            count: parseInt(item.book_count || 0),
          }));
          setCategories(topCategories);
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard summary:", error);
    }
  };

  const fetchAlertMetrics = async () => {
    try {
      const resp = await DashboardApi.fetchAll();
      const data = resp?.data || {};

      console.log("Alert Metrics Data:resp", resp);

      setMetrics(prev => ({
        ...prev,
        dueSoonCount: data.total_due_soon || 0,
        overdueCount: data.overdue_books || 0,
        fineCollectedThisMonth: data.fine_collected_this_month || 0,
        damagedCount: data?.damaged_missing_books || 0,
      }));
    } catch (err) {
      console.error("Error fetching alert metrics:", err);
    }
  };
  const fetchLibraryDetails = async () => {
    try {
      // const bookApi = new DataApi("book");
      const issueApi = new DataApi("bookissue");
      const settingsApi = new DataApi("librarysettings");
      const cardApi = new DataApi("librarycard");
      const booksResp = await DashboardApi.fetchBooks();
      console.log("booksResp", booksResp);

      // ================= FETCH BOOKS =================
      // const booksResp = await bookApi.fetchAll();

      // 👇 Actual books array yahi hai
      const books = booksResp?.data || [];

      console.log("Normalized Books:", books);
      console.log("Is Array:", Array.isArray(books));

      let availableCopies = 0;
      let totalCopies = 0;

      const booksWithAvailability = books.map((b) => {
        console.log("b", b);

        const total = Number(b.total_copies || 0);
        const available = Number(b.available_count || 0);
        const lost = Number(b.lost_copies || 0);
        const issued = Number(b.borrowed_count || 0);
        const damaged = Number(b.damaged_count || 0);

        totalCopies += total;
        availableCopies += available;

        return {
          title: b.title || "Unknown",
          available_copies: available,
          lost_copies: lost,
          issued_copies: issued,
          damaged_copies: damaged,
          total_copies: total
        };
      });
      console.log("booksWithAvailability->", booksWithAvailability);

      //================= TOP 10 AVAILABLE =================
      const sortedBooks = booksWithAvailability
        .sort((a, b) => b.available_copies - a.available_copies)
        .slice(0, 10);

      console.log("Top 10 Books:", sortedBooks);

      setTopAvailableBooks(sortedBooks);

      // ================= FETCH ACTIVE ISSUES =================
      const issuesResp = await issueApi.get("/active");

      const activeIssues =
        issuesResp?.data?.data ||
        issuesResp?.data?.rows ||
        issuesResp?.data ||
        [];

      const issuedCount = Array.isArray(activeIssues)
        ? activeIssues.length
        : 0;

      // ================= UPDATE DASHBOARD METRICS =================
      setMetrics((prev) => ({
        ...prev,
        // availableBooks: availableCopies,
        issuedBooks: issuedCount,
        // totalBooks: totalCopies
      }));

      // ================= FETCH SETTINGS =================
      let cardLimit = 6;

      try {
        const settingsResp = await settingsApi.get("/all");

        const settingsData =
          settingsResp?.data?.data ||
          settingsResp?.data ||
          {};

        cardLimit = Number(
          settingsData.max_books_per_card ??
          settingsData.max_books ??
          settingsData?.max_books_per_card?.setting_value
        ) || cardLimit;

      } catch (err) {
        console.warn("Could not fetch card limit:", err);
      }

      setCardLimitSetting(cardLimit);

      // ================= FETCH CARD DETAILS =================
      await fetchCardDetails(cardApi, issueApi, cardLimit);

    } catch (error) {
      console.error("Error fetching library details:", error);
    }
  };
  // const fetchLibraryDetails = async () => {
  //   try {
  //     const bookApi = new DataApi("book");
  //     const issueApi = new DataApi("bookissue");
  //     const settingsApi = new DataApi("librarysettings");
  //     const cardApi = new DataApi("librarycard");

  //     // ---------------- FETCH BOOKS ----------------
  //     const booksResp = await bookApi.fetchAll();

  //     const books = Array.isArray(booksResp?.data)
  //       ? booksResp.data
  //       : (booksResp?.data?.rows || booksResp || []);

  //     console.log("books", books);
  //     console.log(" Array.isArray(books.data.data.data) ", Array.isArray(books.data.data.data));

  //     let availableCopies = 0;
  //     let totalCopies = 0;

  //     const booksWithAvailability = Array.isArray(books.data.data.data)
  //       ? books.data.data.data.map((b) => {
  //         console.log(b, "bbb");

  //         const total = Number(b.total_copies || 0);
  //         const available = Number(b.available_copies || 0);
  //         console.log("booksWithAvailability", booksWithAvailability);

  //         totalCopies += total;
  //         availableCopies += available;

  //         return {
  //           title: b.title || "Unknown",
  //           available_copies: available,
  //           total_copies: total
  //         };
  //       })
  //       : [];

  //     // ---------------- TOP 10 AVAILABLE ----------------
  //     const sortedBooks = booksWithAvailability
  //       .sort((a, b) => b.available_copies - a.available_copies)
  //       .slice(0, 10);
  //     console.log("sortedBooks ->", sortedBooks);

  //     setTopAvailableBooks(sortedBooks);

  //     // ---------------- FETCH ACTIVE ISSUES ----------------
  //     const issuesResp = await issueApi.get("/active");

  //     const activeIssues = Array.isArray(issuesResp?.data)
  //       ? issuesResp.data
  //       : (issuesResp?.data?.rows || issuesResp || []);

  //     const issuedCount = activeIssues.length;

  //     // ---------------- UPDATE DASHBOARD METRICS ----------------
  //     setMetrics(prev => ({
  //       ...prev,
  //       availableBooks: availableCopies,
  //       issuedBooks: issuedCount,
  //       totalBooks: totalCopies
  //     }));

  //     // ---------------- FETCH SETTINGS ----------------
  //     let cardLimit = 6;

  //     try {
  //       const settingsResp = await settingsApi.get("/all");
  //       const settingsData =
  //         settingsResp?.data?.data || settingsResp?.data || settingsResp;

  //       if (settingsData) {
  //         cardLimit = Number(
  //           settingsData.max_books_per_card ??
  //           settingsData.max_books ??
  //           settingsData.max_books_per_card?.setting_value
  //         ) || cardLimit;
  //       }
  //     } catch (err) {
  //       console.warn("Could not fetch card limit:", err);
  //     }

  //     setCardLimitSetting(cardLimit);

  //     // ---------------- FETCH CARD DETAILS ----------------
  //     await fetchCardDetails(cardApi, issueApi, cardLimit);

  //   } catch (error) {
  //     console.error("Error fetching library details:", error);
  //   }
  // };

  const fetchCardDetails = async (cardApi, issueApi, currentLimit) => {
    try {
      const cardsResp = await cardApi.fetchAll();
      const issuesResp = await issueApi.get("/active");

      const cards = Array.isArray(cardsResp?.data) ? cardsResp.data :
        (cardsResp?.data?.rows || cardsResp || []);
      const activeIssues = Array.isArray(issuesResp?.data) ? issuesResp.data :
        (issuesResp?.data?.rows || issuesResp || []);

      const countsByCard = {};
      activeIssues.forEach((issue) => {
        const cid = issue.card_id || issue.cardId || issue.cardid;
        if (cid) {
          countsByCard[cid] = (countsByCard[cid] || 0) + 1;
        }
      });

      const details = cards.map((c) => {
        const issued = countsByCard[c.id] || 0;
        const remaining = Math.max(0, currentLimit - issued);
        return {
          id: c.id,
          user_name: c.user_name || c.userName || `Card ${c.card_number}` || "Unknown",
          issued: issued,
          remaining: remaining
        };
      });

      details.sort((a, b) => b.issued - a.issued);
      setCardDetails(details.slice(0, 10));

    } catch (error) {
      console.error("Error fetching card details:", error);
    }
  };

  const handleCardClick = (type) => {
    switch (type) {
      case 'dueSoon':
        navigate("/booksubmit?filter=due_soon");
        break;
      case 'overdue':
        navigate("/booksubmit?filter=overdue");
        break;
      default:
        navigate("/book");
    }
  };

  const handleMemberClick = (memberId) => {
    navigate(`/librarycard/${memberId}`);
  };

  const handleTopCategoriesClick = () => {
    navigate("/classification");
  };

  const handleQuickStatsClick = () => {
    navigate("/book");
  };

  const handleUpcomingDueClick = () => {
    navigate("/booksubmit?filter=due_soon");
  };

  const getChartConfig = (filename) => ({
    toolbar: {
      show: true,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
      },
      export: {
        csv: {
          filename: filename,
          headerCategory: "Category",
          columnDelimiter: ','
        },
        svg: {
          filename: filename
        },
        png: {
          filename: filename
        }
      }
    }
  });
  const funnelChartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: true,
      fontFamily: 'inherit',
      toolbar: getChartConfig("Books_Inventory_Report").toolbar
    },

    plotOptions: {
      bar: {
        borderRadius: 5,
        horizontal: false
      }
    },

    colors: [
      '#11439b', 
      '#c9e9fc',  
      '#ff0000',
      '#4b5563'   
    ],

    xaxis: {
      categories: topAvailableBooks.map(b =>
        b.title.length > 18
          ? b.title.substring(0, 18) + "..."
          : b.title
      )
    },

    yaxis: {
      title: {
        text: 'Total Copies'
      }
    },

    legend: {
      position: 'top'
    },

    tooltip: {
      shared: true,
      intersect: false
    }
  };

  const funnelChartSeries = [
    {
      name: 'Available',
      data: topAvailableBooks.map(b => Number(b.available_copies || 0))
    },
    {
      name: 'Issued',
      data: topAvailableBooks.map(b => Number(b.issued_copies || 0))
    },
    {
      name: 'Damaged',
      data: topAvailableBooks.map(b => Number(b.damaged_copies || 0))
    },
    {
      name: 'Lost',
      data: topAvailableBooks.map(b => Number(b.lost_copies || 0))
    }
  ];
  const donutOptions = {
    chart: {
      type: "pie",
      height: 220,
      width: 320,
      fontFamily: 'inherit',
      toolbar: getChartConfig("Inventory_Status_Report").toolbar,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },

    labels: ['Issued', 'Available', 'Overdue', 'Damaged'],
    colors: [SUCCESS_COLOR, ACCENT_COLOR, WARNING_COLOR, DANGER_COLOR],

    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      verticalAlign: "top",
      fontSize: '12px',
      fontFamily: 'inherit',
      markers: {
        width: 12,
        height: 12,
        radius: 8
      },
    },

    tooltip: {
      theme: 'light',
      y: {
        formatter: (val) => {
          const total = Number(metrics.total_copies) || (Number(metrics.issuedBooks || 0) + Number(metrics.availableBooks || 0) + Number(metrics.damagedCount || 0));
          const pct = total ? Math.round((Number(val) / total) * 100) : 0;
          return `${formatNumber(val)} copies (${pct}%)`;
        }
      }
    },

    dataLabels: {
      enabled: true
    }
  };

  const calculateDonutSeries = () => {
    // Return raw counts in order: Issued, Available, Overdue, Damaged
    let issued = Number(metrics.issuedBooks ?? 0) || 0;
    const available = Number(metrics.availableBooks ?? 0) || 0;
    const overdue = Number(metrics.overdueCount ?? 0) || 0;
    const damaged = Number(metrics.damagedCount ?? 0) || 0;

    const totalFromMetric = Number(metrics.total_copies) || 0;
    let total = totalFromMetric || (issued + available + damaged);

    // If total is available and issued isn't provided, derive it
    if (!Number(metrics.issuedBooks) && total > 0) {
      issued = Math.max(0, total - available - damaged);
    }

    // If totals still inconsistent, recompute total as sum
    if (!totalFromMetric) {
      total = issued + available + damaged;
    }

    return [issued, available, overdue, damaged];
  };

  const donutChartSeries = calculateDonutSeries();

  const summaryCards = [
    {
      title: "Total Books",
      value: formatNumber(metrics.totalTitles || metrics.totalBooks),
      icon: "fa-book",
      color: PRIMARY_COLOR,
      bgColor: "#e0e7ff",
      type: "totalBooks"
    },
    {
      title: "Book Copies Availability",
      value: `${formatNumber(metrics.availableBooks)} / ${formatNumber(metrics.total_copies)}`,
      icon: "fa-layer-group",
      color: ACCENT_COLOR,
      bgColor: "#e0e7ff",
      type: "copiesStatus"
    },
    {
      title: "Due Soon",
      value: formatNumber(metrics.dueSoonCount),
      icon: "fa-clock",
      color: WARNING_COLOR,
      bgColor: "#fff7ed",
      type: "dueSoon"
    },
    {
      title: "Overdue",
      value: formatNumber(metrics.overdueCount),
      icon: "fa-circle-exclamation",
      color: DANGER_COLOR,
      bgColor: "#fef2f2",
      type: "overdue"
    },
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        {/* <Loader /> */}
        <span className="loader"></span>
      </div>
    );
  }

  if (userRole === "STUDENT") {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "20px" }}>
        <ScrollToTop />
        <Container fluid>
          <Card style={{
            ...styles.card,
            background: `linear - gradient(135deg, ${PRIMARY_COLOR} 0 %, ${INFO_COLOR} 100 %)`,
            color: "white",
            marginBottom: "30px",
            border: 'none'
          }}>
            <Card.Body className="p-4">
              <h1 className="fw-bolder mb-2" style={{ fontSize: '24px' }}>
                Welcome Back, {userInfo?.firstname || 'Student'}! 👋
              </h1>
              <p className="mb-0 opacity-75" style={{ fontSize: '16px' }}>
                Your personalized library dashboard is ready. Check your borrowed books and upcoming deadlines.
              </p>
            </Card.Body>
          </Card>

          <Row>
            <Col lg={8} className="mx-auto">
              <Card style={styles.card}>
                <Card.Header style={styles.cardHeader}>
                  <h5 className="fw-bold mb-0">Your Currently Issued Books</h5>
                </Card.Header>
                <Card.Body>
                  <div className="text-center py-5">
                    <i className="fa-solid fa-book-open-reader fa-3x text-muted mb-3"></i>
                    <p className="text-muted">No books currently issued</p>
                    <button className="btn btn-primary mt-2" onClick={() => navigate("/book")}>
                      Browse Library
                    </button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "16px" }}>
      <ScrollToTop />
      <Container fluid className="px-2 py-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6>
              📚 Real-Time Analytics For Efficient Library Management
            </h6>
          </div>
        </div>

        {/* 1. Core Library Inventory - Clickable Cards */}
        <Row className="mb-3 g-2">
          {/* LEFT COLUMN – Summary Cards */}
          {/* <Col xl={6} lg={6} md={12}>
            <Row className="g-2 h-75"> */}
          {summaryCards.map((card, index) => (
            <Col xl={3} lg={6} md={6} sm={12} key={index}>
              <InteractiveCard
                onClick={() => handleCardClick(card.type)}
                style={
                  card.type === "dueSoon" || card.type === "overdue"
                    ? { borderLeft: `4px solid ${card.color}` }
                    : {}
                }
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p
                        className="mb-1 text-uppercase"
                        style={{
                          fontSize: "10px",
                          fontWeight: "700",
                          color: "#64748b",
                        }}
                      >
                        {card.title}
                      </p>

                      <h2
                        className="mb-0"
                        style={{
                          color: card.color,
                          fontSize: "20px",
                          fontWeight: "800",
                        }}
                      >
                        {card.value}
                      </h2>
                    </div>

                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        backgroundColor: card.bgColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className={`fa-solid ${card.icon}`}
                        style={{ fontSize: "18px", color: card.color }}
                      ></i>
                    </div>
                  </div>
                </Card.Body>
              </InteractiveCard>
            </Col>
          ))}
          {/* </Row>
          </Col> */}

          {/* RIGHT COLUMN – Dropdown / Other Content */}
          {/* <Col xl={6} lg={6} md={12} className="h-25">
            <PlanDropdownCard />
          </Col> */}
        </Row>

        {/* 2. Urgent Actions */}
        <div style={styles.sectionTitle}>
          <i className="fa-solid fa-bell" style={{ color: WARNING_COLOR, fontSize: '14px' }}></i>
          Urgent Actions
        </div>

        {/* 3. Main Charts Section */}
        <Row className="mb-3 g-2">
          {/* Latest Members Card */}
          <Col lg={4}>
            <InteractiveCard onClick={handleUpcomingDueClick}>
              <Card.Header style={styles.cardHeader}>
                <div className="d-flex justify-content-between align-items-center ">
                  <div >
                    <h6 className="fw-bold m-0 text-dark" style={{ fontSize: '14px' }}>
                      Members with Upcoming Due Dates
                    </h6>
                    <small className="text-muted" style={{ fontSize: '11px' }}>
                      Due in next 48 hours
                    </small>
                  </div>
                  <Badge className="px-2 py-1" style={{
                    borderRadius: '30px',
                    fontSize: '9px',
                    fontWeight: 600,
                    background: WARNING_COLOR,
                    color: 'white'
                  }}>
                    {upcomingDueMembers.length} DUE
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0 ">
                <div className="list-group list-group-flush">
                  {upcomingDueMembers.length > 0 ? (
                    upcomingDueMembers.slice(0, 5).map((member, idx) => {
                      console.log("memember", member)
                      const statusColor = getDueStatusColor(member.due_date);
                      return (
                        <div
                          key={member.id}
                          className="list-group-item d-flex align-items-center justify-content-between px-2 py-2 border-light mt-3"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMemberClick(member.id);
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="position-relative me-2">
                              {/* <img
                                src={member.image}

                                onError={(e) => {
                                  e.target.onerror = null;

                                }}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "2px solid #e2e8f0"
                                }}
                              /> */}
                              {/* <div style={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                background: statusColor,
                                border: "2px solid white"
                              }}></div> */}
                            </div>
                            <div>
                              <p className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>
                                {member.name}
                              </p>
                              <small className="text-muted" style={{ fontSize: '11px' }}>
                                <i className="fa-solid fa-calendar-days me-1"></i>
                                Due on <span style={{ color: statusColor, fontWeight: 600 }}>
                                  {member.due_date}
                                </span>
                              </small>
                              <small className="d-block text-muted" style={{ fontSize: '10px' }}>
                                <i className="fa-solid fa-book me-1"></i>
                                {member.due_count} book{member.due_count > 1 ? 's' : ''}
                              </small>
                            </div>
                          </div>
                          <div className="text-end">
                            <Badge style={{
                              background: statusColor,
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '9px'
                            }} className="rounded-pill px-2 py-1">
                              DUE SOON
                            </Badge>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-3 text-muted">
                      <i className="fa-solid fa-calendar-check fa-lg mb-2"></i>
                      <p className="mb-0" style={{ fontSize: '12px' }}>No upcoming due dates</p>
                    </div>
                  )}
                </div>
                <div className="px-2 py-1 border-top">
                  <div className="text-center">
                    <small className="fw-semibold" style={{ color: PRIMARY_COLOR, fontSize: '11px', cursor: 'pointer' }}>
                      <i className="fa-solid fa-magnifying-glass me-1"></i>
                      Click to view all due books
                    </small>
                  </div>
                </div>
              </Card.Body>
            </InteractiveCard>
          </Col>

          {/* Funnel Chart */}
          <Col lg={8}>
            <Card style={styles.card} >
              {/* <Card.Header style={styles.cardHeader}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="fw-bold m-0 text-dark" style={{ fontSize: '14px' }}>Books with Highest Available Stock</h6>
                    <small className="text-muted" style={{ fontSize: '11px' }}>Top 10 books by available copies</small>
                  </div>
                </div>
              </Card.Header> */}

              {/* <Card.Body className="p-2 ">
                <span className="fw-bold mb- text-dark " style={{ fontSize: '14px' }}>Books with Highest Available Stock </span>
                <span className="text-muted" style={{ fontSize: '11px' }}>Top 10 books by available copies</span>

                {topAvailableBooks.length > 0 ? (
                  <Chart
                    options={funnelChartOptions}
                    series={funnelChartSeries}
                    type="bar"
                    height={280}
                  />
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center py-4 text-muted">
                    <i className="fa-solid fa-book-open-reader fa-2x mb-2"></i>
                    <small>No inventory data available</small>
                  </div>
                )}
                <div className="mt-2 text-center ">
                  <small className="text-muted" style={{ fontSize: '10px' }}>
                    <i className="fa-solid fa-circle-info me-1"></i>
                    Hover for details | Click toolbar for export options (PNG, SVG, CSV)
                  </small>
                </div>
              </Card.Body> */}
              <Card.Body className="p-2">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-bold text-dark ms-3" style={{ fontSize: '14px' }}>
                    Books with Highest Available Stock
                  </span>
                  <span className="text-muted me-2" style={{ fontSize: '11px' }}>
                    Top 10 books by available copies
                  </span>
                </div>

                {topAvailableBooks.length > 0 ? (
                  <Chart
                    options={funnelChartOptions}
                    series={funnelChartSeries}
                    type="bar"
                    height={280}
                  />
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center py-4 text-muted">
                    <i className="fa-solid fa-book-open-reader fa-2x mb-2"></i>
                    <small>No inventory data available</small>
                  </div>
                )}

                {/* <div className="mt-2 text-center">
                  <small className="text-muted" style={{ fontSize: '10px' }}>
                    <i className="fa-solid fa-circle-info me-1"></i>
                    Hover for details | Click toolbar for export options (PNG, SVG, CSV)
                  </small>
                </div> */}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 4. Secondary Analytics */}
        <Row className="g-2">
          {/* Top Categories - Clickable Card */}
          <Col lg={4} sm={6}>
            <InteractiveCard onClick={handleTopCategoriesClick}>
              <Card.Header style={styles.cardHeader}>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="fw-bold m-0 text-dark" style={{ fontSize: '14px' }}>Top Categories by Stock</h6>
                  <Badge className="px-2 py-1" style={{
                    borderRadius: '30px',
                    fontSize: '9px',
                    fontWeight: 600,
                    background: PRIMARY_COLOR,
                    color: 'white',
                    cursor: 'pointer'
                  }}>
                    VIEW ALL
                  </Badge>
                </div>
                <small className="text-muted" style={{ fontSize: '11px' }}>Most populated categories</small>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="list-group list-group-flush">
                  {categories.length > 0 ? categories.slice(0, 5).map((cat, idx) => (
                    <div key={idx} className="list-group-item d-flex align-items-center justify-content-between px-2 py-2 border-light">
                      <div className="d-flex align-items-center">
                        <div className="me-2 rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: 32,
                            height: 32,
                            background: '#e0e7ff',
                            color: PRIMARY_COLOR
                          }}>
                          <i className={`fa - solid ${cat.icon} `}></i>
                        </div>
                        <span className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                          {cat.name.length > 18 ? cat.name.substring(0, 18) + "..." : cat.name}
                        </span>
                      </div>
                      <Badge style={{
                        background: PRIMARY_COLOR,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '11px'
                      }} className="rounded-pill px-2 py-1">
                        {formatNumber(cat.count)}
                      </Badge>
                    </div>
                  )) : (
                    <div className="text-center py-3 text-muted">
                      <i className="fa-solid fa-tags fa-lg mb-2"></i>
                      <p className="mb-0" style={{ fontSize: '12px' }}>No category data available</p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </InteractiveCard>
          </Col>

          {/* Pie Chart */}
          <Col lg={4} sm={6}>

            <Card style={styles.card}>
              <Card.Body className="text-center p-2">
                {/* <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>
                    Books Copies Status
                  </h6>

                </div> */}

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-bold text-dark ms-2" style={{ fontSize: '14px' }}>
                    Books Copies Status
                  </span>
                  <span className="text-muted me-2" style={{ fontSize: '11px' }}>
                    All Statuses of Book Copies
                  </span>
                </div>


                <Chart options={donutOptions} series={donutChartSeries} type="pie" width={380} />
                {/* <div className="mt-2 d-flex justify-content-center flex-wrap gap-3">
                  {(() => {
                    const series = donutChartSeries || [];
                    const labels = donutOptions.labels || ['Issued','Available','Overdue','Damaged'];
                    const colors = donutOptions.colors || [SUCCESS_COLOR, ACCENT_COLOR, WARNING_COLOR, DANGER_COLOR];
                    const total = Number(metrics.total_copies) || series.reduce((s, v) => s + Number(v || 0), 0) || 1;
                    return labels.map((lab, idx) => (
                      <div key={lab} className="d-flex flex-column align-items-center" style={{minWidth:100}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{width:10,height:10,background:colors[idx] || '#ccc',borderRadius:'50%'}}></div>
                          <small className="text-muted">{lab}</small>
                        </div>
                        <div className="fw-bold">{formatNumber(series[idx] || 0)}</div>
                        <small className="text-muted">{Math.round(((Number(series[idx]||0))/total)*100)}%</small>
                      </div>
                    ));
                  })()}
                </div> */}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;